/**
 * Ponto ÚNICO de obtenção do access token da aplicação.
 *
 * Existe por causa de um incidente real: com duas abas abertas, o access token
 * de 15 min expirava, os pollers das duas abas tomavam 401 no mesmo segundo e as
 * duas chamavam `/auth/refresh` com o mesmo refresh token. A segunda apresentava
 * um token que a primeira já havia rotacionado, o backend interpretava como
 * roubo e revogava a família inteira — derrubando as duas sessões. Um usuário
 * chegou a relogar todo dia por semanas.
 *
 * Três camadas resolvem isso, nesta ordem:
 *   1. `inflight` — deduplica dentro da aba.
 *   2. `BroadcastChannel` — as abas pedem o token umas às outras antes de ir à
 *      rede, e a que renova avisa as demais.
 *   3. `navigator.locks` — serializa a renovação ENTRE abas, com re-checagem
 *      dentro do lock (o lock morre junto com a aba, então re-checar é
 *      obrigatório, não opcional).
 *
 * Além disso o token é renovado ANTES de expirar, então o 401 deixa de ser o
 * gatilho normal e vira só rede de segurança.
 *
 * O access token vive apenas em memória. O refresh token não é mais visível ao
 * JavaScript: ele fica num cookie httpOnly first-party emitido pelo BFF de auth.
 */

/** Base das rotas de auth. Em produção aponta para o BFF same-origin. */
export const AUTH_BASE = process.env.REACT_APP_AUTH_URL || '/api/auth';

const LOCK_NAME = 'ipcom-auth-refresh';
const CHANNEL_NAME = 'ipcom-auth';

/** Renova com esta antecedência do exp. Cobre relógio fora de sincronia e rede lenta. */
const SKEW_MS = 90_000;
/** Espera máxima por um peer responder com um token fresco antes de ir à rede. */
const PEER_WAIT_MS = 200;
/** Espalha as renovações no tempo para várias abas não acordarem juntas. */
const JITTER_MS = 20_000;

let accessToken: string | null = null;
let expiresAtMs = 0;
let inflight: Promise<string> | null = null;
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;
let cachedUserId: number | null = null;
const sessionLostHandlers = new Set<() => void>();

/** Avisa TODOS os inscritos. Um handler que estoura não pode calar os outros. */
function notifySessionLost(): void {
  sessionLostHandlers.forEach((handler) => {
    try {
      handler();
    } catch {
      /* um assinante quebrado não derruba os demais */
    }
  });
}

/**
 * Momento até o qual não adianta tentar renovar de novo.
 *
 * Sem isto, com a sessão perdida cada requisição pendente dispararia uma nova
 * chamada a /auth/refresh (o 401 da resposta ainda tenta uma segunda vez), o que
 * viraria uma rajada contra o rate limit — que é exatamente o comportamento de
 * aba órfã que gerou 1545 chamadas em um dia.
 */
let refreshBlockedUntil = 0;
const REFRESH_BACKOFF_MS = 10_000;

export class SessionExpiredError extends Error {
  constructor() {
    super('Sessão expirada');
    this.name = 'SessionExpiredError';
  }
}

const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

channel?.addEventListener('message', (event: MessageEvent) => {
  const message = event.data;
  if (!message || typeof message !== 'object') return;

  if (message.type === 'token' && message.expiresAtMs > expiresAtMs) {
    applyToken(message.accessToken, message.expiresAtMs);
  } else if (message.type === 'request' && isFresh()) {
    channel.postMessage({ type: 'token', accessToken, expiresAtMs });
  } else if (message.type === 'session-lost') {
    clearMemory();
    notifySessionLost();
  }
});

function isFresh(): boolean {
  return !!accessToken && Date.now() < expiresAtMs - SKEW_MS;
}

function decodeExp(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : Date.now() + 15 * 60 * 1000;
  } catch {
    // Token opaco ou malformado: assume o TTL padrão do backend.
    return Date.now() + 15 * 60 * 1000;
  }
}

function applyToken(token: string, expiresAt?: number): void {
  accessToken = token;
  expiresAtMs = expiresAt ?? decodeExp(token);
  scheduleProactiveRefresh();
}

function clearMemory(): void {
  accessToken = null;
  expiresAtMs = 0;
  cachedUserId = null;
  if (proactiveTimer) clearTimeout(proactiveTimer);
  proactiveTimer = null;
}

/**
 * Ponte de migração: o front antigo guardava os tokens em localStorage. Na
 * primeira renovação depois do deploy, mandamos o token legado uma única vez
 * para o backend trocar por cookie httpOnly. Sem isso, todo mundo seria
 * deslogado no momento do deploy do front.
 */
function legacyRefreshBody(): Record<string, string> {
  try {
    const legacy = localStorage.getItem('refresh_token');
    return legacy ? { refresh_token: legacy } : {};
  } catch {
    return {};
  }
}

function clearLegacyStorage(): void {
  try {
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('access_token');
  } catch {
    /* modo privado pode recusar acesso ao storage */
  }
}

async function askPeersForToken(): Promise<boolean> {
  if (!channel) return false;
  channel.postMessage({ type: 'request' });

  const startedAt = Date.now();
  while (Date.now() - startedAt < PEER_WAIT_MS) {
    if (isFresh()) return true;
    await new Promise((resolve) => setTimeout(resolve, 20));
  }
  return isFresh();
}

async function performRefresh(): Promise<string> {
  const response = await fetch(`${AUTH_BASE}/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(legacyRefreshBody()),
  });

  if (!response.ok) {
    // 429 não é sessão perdida: é excesso de tentativas. Só segura a mão.
    if (response.status !== 429) {
      clearMemory();
      clearLegacyStorage();
      channel?.postMessage({ type: 'session-lost' });
      notifySessionLost();
    }
    refreshBlockedUntil = Date.now() + REFRESH_BACKOFF_MS;
    throw new SessionExpiredError();
  }

  const data = await response.json();

  // Defesa contra cookie tossing: um subdomínio irmão de ipcom.app.br poderia
  // plantar um cookie de refresh de outra conta. Se o dono da sessão mudou no
  // meio do caminho, derruba tudo em vez de seguir com a identidade trocada.
  if (data.user?.id != null) {
    if (cachedUserId != null && data.user.id !== cachedUserId) {
      clearMemory();
      channel?.postMessage({ type: 'session-lost' });
      notifySessionLost();
      throw new SessionExpiredError();
    }
    cachedUserId = data.user.id;
  }

  // A partir daqui o refresh token vive só no cookie httpOnly.
  clearLegacyStorage();
  refreshBlockedUntil = 0;

  applyToken(data.access_token);
  channel?.postMessage({ type: 'token', accessToken, expiresAtMs });
  return accessToken as string;
}

/**
 * Devolve um access token válido, renovando se necessário.
 * `force` ignora o cache — use ao tratar um 401 real.
 */
export async function ensureFreshToken(force = false): Promise<string> {
  if (!force && isFresh()) return accessToken as string;
  if (inflight) return inflight;
  if (Date.now() < refreshBlockedUntil) throw new SessionExpiredError();

  inflight = (async () => {
    if (!force && (await askPeersForToken())) return accessToken as string;

    const run = async (): Promise<string> => {
      // Double-checked read: outra aba pode ter renovado enquanto esperávamos o
      // lock. Sem isto, N abas fariam N renovações em sequência.
      if (!force && isFresh()) return accessToken as string;
      if (!force && (await askPeersForToken())) return accessToken as string;
      return performRefresh();
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
      return navigator.locks.request(LOCK_NAME, run) as Promise<string>;
    }
    // Safari < 15.4 e afins: sobra o dedupe por aba. A janela de graça do
    // backend existe exatamente para cobrir este caso.
    return run();
  })().finally(() => {
    inflight = null;
  });

  return inflight;
}

function scheduleProactiveRefresh(): void {
  if (proactiveTimer) clearTimeout(proactiveTimer);
  if (!expiresAtMs) return;

  const jitter = Math.random() * JITTER_MS;
  const delay = Math.max(5_000, expiresAtMs - SKEW_MS - Date.now() - jitter);
  proactiveTimer = setTimeout(() => {
    ensureFreshToken().catch(() => {
      /* sessão perdida já foi sinalizada por notifySessionLost */
    });
  }, delay);
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // NÃO renova direto aqui. Aba em background tem timer estrangulado e laptop
    // suspenso não dispara timer nenhum, então N abas voltam ao foco no mesmo
    // instante — era esse o pico de corrida. Reagenda, e só busca token se de
    // fato venceu; quem chegar depois encontra o token fresco no double-check.
    scheduleProactiveRefresh();
    if (!isFresh()) {
      ensureFreshToken().catch(() => undefined);
    }
  });

  window.addEventListener('online', scheduleProactiveRefresh);
}

/** Chamado no login: guarda o access token recém-emitido. */
export function setSessionToken(token: string, userId?: number): void {
  if (userId != null) cachedUserId = userId;
  refreshBlockedUntil = 0; // login novo destrava o backoff
  applyToken(token);
  channel?.postMessage({ type: 'token', accessToken, expiresAtMs });
}

/** Invalida o token em memória sem encerrar a sessão (usado ao tratar 401). */
export function invalidateAccessToken(): void {
  accessToken = null;
  expiresAtMs = 0;
}

/** Encerra a sessão local e avisa as outras abas. */
export function clearSession(): void {
  clearMemory();
  clearLegacyStorage();
  channel?.postMessage({ type: 'session-lost' });
}

/** Token atual sem ir à rede. Devolve null se não houver token utilizável. */
export function getAccessTokenSync(): string | null {
  return isFresh() ? accessToken : null;
}

/**
 * Inscreve um callback para quando a sessão morrer.
 *
 * É uma LISTA, não um slot. Enquanto era slot único, `App.tsx` e `AuthContext`
 * registravam cada um o seu e o segundo apagava o primeiro — como o AuthProvider
 * envolve o App, o efeito do filho roda antes e quem sobrevivia era o do
 * contexto. Resultado: o `setIsAuth(false)` do App nunca rodava, a tela seguia
 * inteira com dados velhos e toda ação nova voltava "Unauthorized", sem nunca
 * cair no login.
 *
 * Devolve a função que desinscreve — use no cleanup do efeito.
 */
export function addSessionLostHandler(handler: () => void): () => void {
  sessionLostHandlers.add(handler);
  return () => {
    sessionLostHandlers.delete(handler);
  };
}
